import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spinner, Badge, Button, Alert } from "react-bootstrap";
import { FaSync, FaChevronRight, FaInbox, FaArrowLeft } from "react-icons/fa";
import sessionStorage from "./utils/sessionStore";
import { apiFetch } from "./utils/auth";
import { isReviewApprover, reviewStatusMeta, statusColor } from "./utils/reviewAccess";
import { PO_CLASS, PO_CLASS_ORDER, classifyPo } from "./utils/poClassification";
import GridPagination from "./components/GridPagination";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// Classify a review row's error status into one or more categories, combining the
// quote-level automated gate (systemCheck) with each held/failed PO's reconcile
// reason. "Ready" quotes get a single clear-to-convert chip; everything else gets
// the specific hold reasons — Under-covered, Part not matched, Amount mismatch, …
function quoteCategories(r) {
  if (r.systemCheck === "Ready") return ["ready"];
  const cats = new Set();
  if (r.systemCheck === "NotReady") cats.add("undercovered");
  (r.heldPos || []).forEach((hp) =>
    cats.add(classifyPo({ held: true, message: hp.extractionError, status: hp.extractionStatus }))
  );
  if (cats.size === 0) cats.add("review");   // held, but no per-PO detail available
  return PO_CLASS_ORDER.filter((k) => cats.has(k));
}

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

const PLAN_PILL = { CASH: "qw-pill-emerald", SPLIT: "qw-pill-indigo", POT: "qw-pill-violet", CTO: "qw-pill-amber" };

// The CS work queue is ordered by where the human's attention is needed:
// PendingReview first (the actual to-do), then the rest for context.
const STATUS_TABS = [
  { value: "PendingReview",    label: "In Review" },
  { value: "ChangesRequested", label: "Returned" },
  { value: "Approved",         label: "Approved" },
  { value: "Creating",         label: "Creating" },
  { value: "OrderCreated",     label: "Created" },
  { value: "CreationFailed",   label: "Failed" },
  { value: "ALL",              label: "All" },
];

// =============================================================================
// QuoteReviewQueue — the Customer-Service conversion review dashboard.
// Lists submitted conversions by status; each row opens the tally/approve view.
// CS sees all; a sales rep is scoped to their own quotes by the backend.
// =============================================================================
function QuoteReviewQueue() {
  const navigate = useNavigate();
  // Deep-link support: /reviews?status=OrderCreated opens straight on that bucket
  // (used by the "review status" link on the Quotes search page). Falls back to
  // "In Review" for a missing/unknown value.
  const [searchParams] = useSearchParams();
  const [status, setStatus]   = useState(() => {
    const requested = searchParams.get("status");
    return STATUS_TABS.some((t) => t.value === requested) ? requested : "PendingReview";
  });

  // Focus support: /reviews?status=X&focus=134076 (from the Quotes page) hunts
  // for that quote by paging forward until it's found, then highlights + scrolls
  // to it. Because the queue is recent-first and unbounded, we page rather than
  // ask the server for its position — bounded by FOCUS_MAX_PAGE so a stale/absent
  // quote can't page forever.
  const FOCUS_MAX_PAGE = 20;
  const [focusQuote, setFocusQuote] = useState(() => {
    const f = searchParams.get("focus");
    return f && /^\d+$/.test(f) ? Number(f) : null;
  });
  const [highlightQuote, setHighlightQuote] = useState(null);
  const focusRowRef = useRef(null);
  // Which page the currently-shown rows belong to — so the hunt only evaluates
  // fresh rows (guards against advancing again before the next page has loaded).
  const [loadedPage, setLoadedPage] = useState(0);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  // Server-side paging (the queue endpoint returns a bare array with no total,
  // so we use simple Next/Prev: a full page means there may be more).
  const [page, setPage]       = useState(1);
  const PAGE_SIZE = 25;

  const approver = isReviewApprover();

  useEffect(() => {
    if (!sessionStorage.getItem("authToken")) navigate("/login");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const parts = [];
      if (status !== "ALL") parts.push(`status=${encodeURIComponent(status)}`);
      parts.push(`page=${page}`);
      parts.push(`pageSize=${PAGE_SIZE}`);
      const r = await apiFetch(`${API_URL}/api/QuoteReview/queue?${parts.join("&")}`);
      const data = await r.json().catch(() => []);
      if (!r.ok) throw new Error(data?.error || data?.message || `HTTP ${r.status}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message); setRows([]);
    } finally {
      setLoading(false);
      setLoadedPage(page);   // mark the rows now on screen as belonging to `page`
    }
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  // Hunt for the focused quote: after each page settles, if it's not on this page
  // and there's another page (and we're under the cap), advance; when found,
  // highlight it and stop; if we run out, give up quietly.
  useEffect(() => {
    // Only evaluate once the rows on screen are the freshly-loaded current page
    // (loadedPage === page), so we never advance twice for one page.
    if (focusQuote == null || loading || loadedPage !== page) return;
    if (rows.some((r) => r.quoteNum === focusQuote)) {
      setHighlightQuote(focusQuote);
      setFocusQuote(null);
    } else if (rows.length >= PAGE_SIZE && page < FOCUS_MAX_PAGE) {
      setPage((p) => p + 1);
    } else {
      setFocusQuote(null);   // not in this bucket (or too deep) — land where we are
    }
  }, [rows, loading, focusQuote, page, loadedPage]);

  // Scroll the highlighted row into view once it's rendered.
  useEffect(() => {
    if (highlightQuote != null && focusRowRef.current) {
      focusRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightQuote, rows]);

  // Keep the highlight until the user clicks away: the first click that isn't on
  // the highlighted row itself (which navigates to its detail) clears it. No timer
  // — it stays put while they look at it.
  useEffect(() => {
    if (highlightQuote == null) return;
    const onDocMouseDown = (e) => {
      if (focusRowRef.current && focusRowRef.current.contains(e.target)) return;
      setHighlightQuote(null);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [highlightQuote]);

  return (
    <div className="qw-root content-halo" style={{ padding: "28px 24px 36px", maxWidth: "min(1080px, 95vw)", margin: "84px auto 56px", borderRadius: 16, minHeight: "calc(100vh - 140px)" }}>
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon"><FaInbox size={20} /></div>
          <div>
            <div className="qw-header-title">Conversion Review Queue</div>
            <div className="qw-header-sub">Shukla Medicals · Customer-Service approvals</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} onClick={() => navigate("/quote")}>
            <FaArrowLeft style={{ marginRight: 6 }} /> Back to Quotes
          </Button>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} onClick={load} disabled={loading}>
            <FaSync style={{ marginRight: 6 }} /> Refresh
          </Button>
        </div>
      </div>

      {!approver && (
        <Alert variant="warning" style={{ fontSize: 13 }}>
          You{"’"}re viewing only your own submitted conversions. Approving and returning are restricted to Customer-Service reviewers.
        </Alert>
      )}

      {/* Status tabs */}
      <div className="qw-card" style={{ marginBottom: 16, padding: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            className={`qw-btn qw-btn-sm ${status === t.value ? "qw-btn-primary" : "qw-btn-ghost"}`}
            onClick={() => { setStatus(t.value); setPage(1); setFocusQuote(null); setHighlightQuote(null); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>}

      {loading || focusQuote != null ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}>
          <Spinner animation="border" size="sm" /> {focusQuote != null ? `Locating quote #${focusQuote}…` : "Loading queue…"}
        </div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}>
          <FaInbox size={28} style={{ opacity: 0.4 }} />
          <div style={{ marginTop: 10, fontSize: 14 }}>Nothing in {STATUS_TABS.find((t) => t.value === status)?.label || status}.</div>
        </div>
      ) : (
        <div className="qw-stack">
          {/* Error-status breakdown — classifies the listed quotes by hold reason
              (a quote is counted under each distinct category it has) so CS sees
              the mix at a glance: "2 Under-covered · 1 Part not matched · 4 Ready". */}
          {(() => {
            const counts = {};
            rows.forEach((r) => quoteCategories(r).forEach((k) => { counts[k] = (counts[k] || 0) + 1; }));
            const chips = PO_CLASS_ORDER.filter((k) => counts[k]);
            return chips.length > 1 ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                {chips.map((k) => (
                  <Badge key={k} bg={PO_CLASS[k].tone} text={PO_CLASS[k].dark ? "dark" : undefined}>
                    {counts[k]} {PO_CLASS[k].label}
                  </Badge>
                ))}
              </div>
            ) : null;
          })()}
          {rows.map((r) => {
            const meta = reviewStatusMeta(r.status, r.termsCode);
            const cats = quoteCategories(r);
            const holdReason = r.status === "CreationFailed"
              ? (r.creationError || r.systemCheckDetail)
              : (r.systemCheck !== "Ready" ? r.systemCheckDetail : null);
            const isFocused = r.quoteNum === highlightQuote;
            return (
              <div
                key={r.quoteNum}
                ref={isFocused ? focusRowRef : undefined}
                className="qw-card-flat"
                style={{
                  padding: 14, cursor: "pointer",
                  ...(isFocused ? { outline: "2px solid #6366F1", boxShadow: "0 0 0 4px rgba(99,102,241,.15)", borderRadius: 10 } : {}),
                }}
                onClick={() => navigate(`/reviews/${r.quoteNum}`, {
                  // Remember the exact origin so the detail's "Back to Queue"
                  // returns to this tab and re-locates/highlights this quote.
                  state: { from: `/reviews?status=${encodeURIComponent(status)}&focus=${r.quoteNum}` },
                })}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span className="qw-mono" style={{ fontWeight: 700, color: "#0F172A", fontSize: 15 }}>#{r.quoteNum}</span>
                      <span className={`qw-pill ${PLAN_PILL[r.planCategory] || "qw-pill-slate"}`}>{r.termsCode || r.planCategory}</span>
                      {cats.map((k) => (
                        <Badge key={k} bg={PO_CLASS[k].tone} text={PO_CLASS[k].dark ? "dark" : undefined}>{PO_CLASS[k].label}</Badge>
                      ))}
                    </div>
                    <div style={{ fontSize: 13.5, color: "#334155", marginTop: 3, fontWeight: 600 }}>{r.customerName || r.custID || "—"}</div>
                    <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
                      {r.poCount} PO{r.poCount === 1 ? "" : "s"}
                      {r.submittedBy ? ` · by ${r.submittedBy}` : ""}
                      {r.reviewerChainText ? ` · ${r.reviewerChainText}` : ""}
                    </div>
                    {holdReason && (
                      <div style={{ fontSize: 11.5, color: r.systemCheck === "NotReady" ? "#B91C1C" : "#92400E", marginTop: 4 }}>
                        {holdReason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ textAlign: "right" }}>
                      <div className="qw-mono" style={{ fontWeight: 700, color: "#0052A5", fontSize: 16 }}>{fmtMoney(r.grandTotal)}</div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: statusColor(meta.kind) }}>{meta.label}</span>
                    </div>
                    <FaChevronRight style={{ color: "#CBD5E1", flexShrink: 0 }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Simple Next/Prev pager (server-side paging; total is unknown, so no
          page numbers). Shown whenever there are rows or we've paged forward. */}
      {!loading && focusQuote == null && (rows.length > 0 || page > 1) && (
        <div style={{ marginTop: 12 }}>
          <GridPagination
            currentPage={page - 1}
            totalPages={0}
            totalCount={null}
            hasNext={rows.length >= PAGE_SIZE}
            showFrom={rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}
            showTo={rows.length ? (page - 1) * PAGE_SIZE + rows.length : 0}
            onGoToPage={(p) => setPage(p + 1)}
          />
        </div>
      )}
    </div>
  );
}

export default QuoteReviewQueue;
